<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation, FreeMode, A11y } from 'swiper/modules'
import { SwiperOptions } from 'swiper/types'
import { uniqueId } from 'lodash-es'
import Toast from '@/components/toast'
import { TOAST, useCustomToastStore } from '@/components/custom-toast'
import Dialog from '@/components/dialog/dialog-modal/dialog.vue'
import { router } from '@/eco-system'
import { useUserStore } from '~/store/user'
import { useUtilDetectAllStore } from '~/store/common/index'
import {
  type DetailGoodsItemWrapperConfig,
  VIEW_TYPE,
  IMAGE_VIEW_TYPE
} from '~/components/common/modules/types/goods-item-type'
import DetailGoodsItem from '~/components/common/modules/units/detail-goods-item.vue'
import DetailGoodsItemWrapper from '~/components/common/modules/units-wrapper/detail-goods-item-wrapper.vue'
import { postWishList, deleteWishList, getExternalMarketingInfo } from '~/api/goods'
import { GoodsInfoList } from '~/api/types/goods'
import { getUsePossToll } from '~/api/event'
import { useStoreGaAttr } from '~/composable/store/ga/use-store-ga-attr'
import { useDetailGaEvent } from '~/pages/goods/composables/ui'
import {
  GA_STORE_COMPONENTS_WRAP_CLASS,
  GA_STORE_COMPONENTS_LOCATION_INFO
} from '~/composable/store/ga/common/store-ga-goods-click-constant'
import { GA_EVENT_TYPE } from '~/composable/store/ga/use-store-ga-e-commerce'
export interface Props {
  title: string
  items?: Array<GoodsInfoList> | null
  bannerType?: boolean
}

const props = withDefaults(defineProps<Props>(), {})

const userStore = useUserStore()
const { custInfo } = storeToRefs(userStore)
const detectAllStore = useUtilDetectAllStore()
const { isDesktop } = storeToRefs(detectAllStore)
const customToastStore = useCustomToastStore()
const { addWishGoogleAnalytics } = useDetailGaEvent()

const localId = `${uniqueId('__goods-list-')}`
const isLogin = computed(() => !!custInfo.value?.loginId)
const dialogRef = ref<InstanceType<typeof Dialog>>()
const setBreakpoints: SwiperOptions['breakpoints'] = isDesktop.value
  ? {
      0: { slidesPerView: 6, spaceBetween: 24 }
    }
  : {
      0: { slidesPerView: 'auto', spaceBetween: 8 }
    }

const toastHandler = (content: string, delay: number, position: string, useHtml?: boolean) => {
  Toast({ content, delay, position, useHtml })
}

async function toggleWish(item: GoodsInfoList) {
  if (!isLogin.value) {
    router.push({ name: 'CustomerLogin' })
    return
  }

  if (item.wishYn !== 'Y') {
    const { data } = await postWishList({ goodsCd: Number(item.goodsCd) })

    if (data.resultCode === '0000') {
      item.wishYn = 'Y'
      customToastStore.open({
        content: TOAST.WISH
      })

      const info = await getExternalMarketingInfo(Number(item.goodsCd))

      let tollPoint = 0
      const tollPointData = await getUsePossToll()
      if (tollPointData.data.resultData) {
        tollPoint = Number(tollPointData.data.resultData.usePossAmt)
      }

      if (info) {
        //GA 찜추가
        addWishGoogleAnalytics(GA_EVENT_TYPE.ADD_WISH, item, info.data.resultData!, tollPoint)
      }
    } else if (data.resultCode === '7820') {
      toastHandler(
        '찜한 상품이 100개를 초과하였습니다.<br> 계속 찜하시려면 찜 목록을 정리해 주세요.',
        2000,
        'bottom',
        true
      )
    }
  } else {
    const { data } = await deleteWishList({ goodsCd: [Number(item.goodsCd)] })

    if (data.resultCode === '0000') {
      item.wishYn = 'N'
      customToastStore.open({
        content: TOAST.UNWISH
      })

      const info = await getExternalMarketingInfo(Number(item.goodsCd))

      let tollPoint = 0
      const tollPointData = await getUsePossToll()
      if (tollPointData.data.resultData) {
        tollPoint = Number(tollPointData.data.resultData.usePossAmt)
      }

      if (info) {
        //GA 찜삭제
        addWishGoogleAnalytics(GA_EVENT_TYPE.DELETE_WISH, item, info.data.resultData!, tollPoint)
      }
    }
  }
}

/**
 * 데이터 가공
 */
function configInfoInit(item: GoodsInfoList): DetailGoodsItemWrapperConfig {
  return {
    ...item,
    viewType: VIEW_TYPE.GRID,
    imageViewType: IMAGE_VIEW_TYPE.CODE,
    isShowWish: true
  }
}

// --------------------------------------------------------------------- [GA]
const { convertDatasetAttribute } = useStoreGaAttr()

const gaNonCompDataSetInfo = computed(() => {
  const gaObject = {
    ...GA_STORE_COMPONENTS_LOCATION_INFO.NON_COMP,
    itemListName: `상품상세 ${props.title} 영역`
  }

  return convertDatasetAttribute(gaObject)
})
</script>

<template>
  <div class="md-pick-best" :class="GA_STORE_COMPONENTS_WRAP_CLASS" v-bind="gaNonCompDataSetInfo">
    <div :id="localId" class="goods-list-wrap">
      <h3 class="title">{{ title }}</h3>

      <div :class="`goods-list ${bannerType ? 'number' : ''}`">
        <Swiper
          :modules="[A11y, FreeMode, Navigation]"
          :free-mode="true"
          :navigation="{ prevEl: `#${localId} .goods-prev-btn`, nextEl: `#${localId} .goods-next-btn` }"
          :breakpoints="setBreakpoints"
        >
          <SwiperSlide v-for="(item, index) in items" :key="index">
            <DetailGoodsItemWrapper :config="configInfoInit(item)" v-slot="{ itemInfo, configInfo }">
              <DetailGoodsItem
                :item="itemInfo"
                :config="configInfo"
                :index-number="index + 1"
                @on-change-wish="toggleWish(item)"
              />
            </DetailGoodsItemWrapper>
          </SwiperSlide>
        </Swiper>
        <div class="swiper-btn-wrap" v-if="isDesktop">
          <button type="button" class="goods-prev-btn">이전으로</button>
          <button type="button" class="goods-next-btn">다음으로</button>
        </div>
      </div>
    </div>
  </div>
  <Dialog ref="dialogRef" />
</template>

<style lang="scss" scoped>
@use '~/pages/goods/scss/goods-md-recommend.scss';
</style>
